import { ArrowRight } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../context";
import { usePageSettled } from "../../lib/deferred";

// Landing hero with rotating partner logos and primary booking CTA.
export default function Hero() {
  const { t } = useTranslation();
  const { isDarkMode } = useDarkMode();
  const logos = useMemo(
    () => [
      "/logos/logo_1.webp",
      // "/logos/logo_2.webp",
      // "/logos/logo_3.webp",
      "/logos/logo_4.webp",
      // "/logos/logo_5.webp",
      "/logos/logo_6.webp",
      // "/logos/logo_7.webp",
      // "/logos/logo_8.webp",
      // "/logos/logo_9.webp",
      // "/logos/logo_10.webp",
      // "/logos/logo_11.webp",
      // "/logos/logo_12.webp",
      // "/logos/logo_13.webp",
      // "/logos/logo_14.webp",
      // "/logos/logo_15.webp",
      // "/logos/logo_16.webp",
      // "/logos/logo_17.webp",
      // "/logos/logo_18.webp",
      "/logos/logo_19.webp",
      // "/logos/logo_20.webp",
      // "/logos/logo_21.webp",
      // "/logos/logo_22.webp",
      // "/logos/logo_23.webp",
      // "/logos/logo_24.webp",
      // "/logos/logo_25.webp",
      "/logos/logo_26.webp",
      // "/logos/logo_27.webp",
      // "/logos/logo_28.webp",
      // "/logos/logo_29.webp",
      // "/logos/logo_30.webp",
      "/logos/logo_31.webp",
      // "/logos/logo_32.webp",
      // "/logos/logo_33.webp",
    ],
    []
  );

  // Pre-generated variants: -xs (420w, phones), -sm (600w, tablets/laptops),
  // original (1024w, large retina desktops). Must match the preload in index.html.
  const logoSrcSet = (src: string) =>
    `${src.replace(/\.webp$/, "-xs.webp")} 420w, ${src.replace(/\.webp$/, "-sm.webp")} 600w, ${src} 1024w`;
  const LOGO_SIZES =
    "(max-width: 767px) 230px, (max-width: 1023px) 320px, (max-width: 1279px) 448px, 512px";

  type Language = {
    language: string;
    flag: string;
    value: [string, string];
  };

  const languages: Language[] = [
    {
      language: "Darija",
      flag: "🇲🇦",
      value: ["Dar", "Zaytoun"],
    },
    {
      language: "English",
      flag: "🇬🇧",
      value: ["House", "of Olives"],
    },
    {
      language: "French",
      flag: "🇫🇷",
      value: ["Maison", "des Oliviers"],
    },
    {
      language: "Dutch",
      flag: "🇳🇱",
      value: ["Huis", "van de Olijven"],
    },
    {
      language: "Spanish",
      flag: "🇪🇸",
      value: ["Casa", "de los Olivos"],
    },
    {
      language: "Italian",
      flag: "🇮🇹",
      value: ["Casa", "degli Ulivi"],
    },
    {
      language: "German",
      flag: "🇩🇪",
      value: ["Haus", "der Oliven"],
    },
    {
      language: "Portuguese",
      flag: "🇵🇹",
      value: ["Casa", "das Oliveiras"],
    },
    {
      language: "Turkish",
      flag: "🇹🇷",
      value: ["Ev", "Zeytin"],
    },
    {
      language: "Arabic",
      flag: "🇸🇦",
      value: ["دار", "الزيتون"],
    },
  ];

  const [currentLogoIndex, setCurrentLogoIndex] = useState(0);
  const [currentLangIndex, setCurrentLangIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Only one of the two layouts is visible; render the logo <img> in that one
  // alone, since an <img> inside a display:none parent still downloads.
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches,
  );
  // Rotations and prefetching wait until the page has settled so they don't
  // compete with the first (LCP) logo or add re-renders during load.
  const settled = usePageSettled();

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Warm the cache for the other logos so rotation swaps are instant.
  useEffect(() => {
    if (!settled) return;
    logos.slice(1).forEach((logoSrc) => {
      const img = new Image();
      img.sizes = LOGO_SIZES;
      img.srcset = logoSrcSet(logoSrc);
    });
  }, [settled, logos]);

  // Logo rotation
  useEffect(() => {
    if (!settled) return;
    const interval = setInterval(() => {
      let newIndex;
      do {
        newIndex = Math.floor(Math.random() * logos.length);
      } while (newIndex === currentLogoIndex && logos.length > 1);

      setCurrentLogoIndex(newIndex);
    }, 4000);

    return () => clearInterval(interval);
  }, [settled, currentLogoIndex, logos.length]);

  // Language rotation
  useEffect(() => {
    if (!settled) return;
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentLangIndex((prev) => (prev + 1) % languages.length);
        setIsTransitioning(false);
      }, 500);
    }, 4000);

    return () => clearInterval(interval);
  }, [settled, languages.length]);

  const scrollToRooms = () => {
    const element = document.getElementById("rooms");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const currentLanguage = languages[currentLangIndex];
  const isRTL = currentLangIndex === 0 || currentLangIndex === 1;

  // Calculate font size based on text length
  const getResponsiveFontSize = (text: [string, string]) => {
    const firstWordLength = text[0].length;
    const secondWordLength = text[1].length;
    const longestWord = Math.max(firstWordLength, secondWordLength);
    const combinedLength = firstWordLength + secondWordLength;

    // Mobile scaling - special handling for short names
    let mobileScale;
    if (combinedLength <= 12) {
      // example: "Dar Zaytoun", "Ev Zeytin", "دار الزيتون"
      mobileScale = "scale-[1.2]";
    } else if (longestWord > 14) {
      mobileScale = "scale-[1]";
    } else if (longestWord > 12) {
      mobileScale = "scale-[1]";
    } else if (longestWord > 10) {
      mobileScale = "scale-[1]";
    } else {
      mobileScale = "scale-100";
    }

    // Desktop scaling
    const desktopScale =
      longestWord > 14
        ? "scale-[0.65]"
        : longestWord > 12
        ? "scale-[0.75]"
        : longestWord > 10
        ? "scale-[0.85]"
        : longestWord > 8
        ? "scale-95"
        : "scale-100";

    return { mobile: mobileScale, desktop: desktopScale };
  };

  const fontScale = getResponsiveFontSize(currentLanguage.value);

  return (
    <section
      id="home"
      className="relative w-full h-auto flex items-center justify-center overflow-hidden bg-gradient-to-b from-white via-[#faf8f5] to-[#ebe0c8] dark:from-[#1a0f0a] dark:via-[#2d1810] dark:to-[#3d2318] transition-colors duration-500 overflow-x-hidden py-12 md:py-16 lg:py-20"
    >
      {/* The animated name below is aria-hidden, so this is the page's real,
          accessible (and SEO) heading — visually hidden, always present. */}
      <h1 className="sr-only">
        {t(
          "hero.srTitle",
          "Dar Zaytoun Bhalil — Boutique Guesthouse in Bhalil, Near Fez, Morocco"
        )}
      </h1>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(199,140,66,0.03)_0%,transparent_60%)] dark:bg-[radial-gradient(circle_at_30%_30%,rgba(199,140,66,0.08)_0%,transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_70%,rgba(90,107,46,0.02)_0%,transparent_60%)] dark:bg-[radial-gradient(circle_at_70%_70%,rgba(90,107,46,0.06)_0%,transparent_60%)]" />

      {/* Continuous Animated Leaves */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(12)].map((_, i) => {
          const delay = i * 0.6;
          const duration = 4 + (i % 3);
          const startY = 10 + (i % 5) * 15;
          // A second, elongated leaf silhouette mixed in every third leaf so
          // the drift doesn't read as one shape copy-pasted twelve times.
          const isVineLeaf = i % 3 === 1;

          return (
            <div
              key={i}
              className="absolute leaf-container"
              style={{
                insetInlineStart: "-5%",
                top: `${startY}%`,
                animationDelay: `${delay}s`,
                animationDuration: `${duration}s`,
              }}
            >
              {/* Spin an HTML wrapper, not the <svg> — SVG animations can't be
                  GPU-composited and would repaint on the main thread. */}
              <span
                className="block leaf-rotate"
                style={{
                  animationDelay: `${delay}s`,
                  animationDuration: `${duration * 0.8}s`,
                }}
              >
              <svg
                width={isVineLeaf ? 20 : 16}
                height={isVineLeaf ? 20 : 16}
                viewBox="0 0 24 24"
                fill="none"
                className="block drop-shadow-sm"
              >
                {isVineLeaf ? (
                  <>
                    <path
                      d="M19 3C12 4 7 9 6 16l-1 5 3-3c6-1 11-6 11-15Z"
                      fill={
                        isDarkMode
                          ? "rgba(220, 189, 159, 0.5)"
                          : "rgba(199, 140, 66, 0.5)"
                      }
                    />
                    <path
                      d="M17 5 5 21M10 12h4M8 15h3M12 9h4"
                      stroke={
                        isDarkMode
                          ? "rgba(220, 189, 159, 0.7)"
                          : "rgba(141, 97, 57, 0.7)"
                      }
                      strokeWidth="0.9"
                      strokeLinecap="round"
                    />
                  </>
                ) : (
                  <>
                    <path
                      d="M12 2C12 2 8 6 8 12C8 15 10 18 12 18C14 18 16 15 16 12C16 6 12 2 12 2Z"
                      fill={
                        isDarkMode
                          ? "rgba(139, 162, 76, 0.5)"
                          : "rgba(90, 107, 46, 0.5)"
                      }
                    />
                    <path
                      d="M12 2C12 2 14 4 16 8C17 10 18 13 16 16C15 17 12 18 12 18"
                      stroke={
                        isDarkMode
                          ? "rgba(139, 162, 76, 0.7)"
                          : "rgba(90, 107, 46, 0.7)"
                      }
                      strokeWidth="1"
                      fill="none"
                    />
                  </>
                )}
              </svg>
              </span>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes leaf-drift {
          0% {
            transform: translateX(0) translateY(0);
            opacity: 0;
          }
          5% {
            opacity: 0.6;
          }
          95% {
            opacity: 0.6;
          }
          100% {
            transform: translateX(var(--leaf-drift-x, 110vw)) translateY(80px);
            opacity: 0;
          }
        }

        /* Leaves drift the other way in RTL so they still cross with the text. */
        html[dir="rtl"] .leaf-container {
          --leaf-drift-x: -110vw;
        }

        @keyframes leaf-spin {
          0% {
            transform: rotate(0deg) scale(1);
          }
          25% {
            transform: rotate(90deg) scale(1.1);
          }
          50% {
            transform: rotate(180deg) scale(1);
          }
          75% {
            transform: rotate(270deg) scale(0.9);
          }
          100% {
            transform: rotate(360deg) scale(1);
          }
        }


        @keyframes light-wave {
          0% {
            transform: translateX(-150%) skewX(-20deg);
          }
          45%, 100% {
            transform: translateX(250%) skewX(-20deg);
          }
        }

        .leaf-container {
          animation: leaf-drift linear infinite;
        }

        .leaf-rotate {
          animation: leaf-spin ease-in-out infinite;
        }
      `}</style>

      <div className="relative w-full max-w-6xl mx-auto px-4 md:px-6 lg:px-12 pt-4 md:pt-12 lg:pt-20">
        {/* Mobile Layout - Vertical */}
        <div className="flex md:hidden flex-col items-center text-center space-y-2">
          <div className="flex items-center justify-center relative mt-4 w-64 h-64">
            {!isDesktop && (
              <img
                src={logos[currentLogoIndex].replace(/\.webp$/, "-xs.webp")}
                srcSet={logoSrcSet(logos[currentLogoIndex])}
                sizes={LOGO_SIZES}
                alt="Dar Zaytoun"
                className="w-64 h-64 object-contain drop-shadow-[0_10px_40px_rgba(90,107,46,0.12)]"
                fetchPriority="high"
                width={256}
                height={256}
              />
            )}
          </div>

          <div className="flex flex-col space-y-1.5 w-full px-4">
            <div className="space-y-2">
              <div
                className={`transition-all duration-500 ${
                  isTransitioning
                    ? "opacity-0 blur-sm scale-95"
                    : "opacity-100 blur-0 scale-100"
                } ${fontScale.mobile} origin-center flex flex-col items-center`}
              >
                <div
                  className="font-serif leading-[0.85]"
                  dir={isRTL ? "rtl" : "ltr"}
                  aria-hidden="true"
                >
                  <span className="text-[#1c1e0e] dark:text-gray-100 text-5xl font-bold tracking-tight transition-colors duration-500 drop-shadow-sm whitespace-nowrap">
                    {currentLanguage.value[0]}{" "}
                  </span>
                  <span className="text-[#5a6b2e] dark:text-olive-400 text-5xl font-bold tracking-tight transition-colors duration-500 drop-shadow-sm whitespace-nowrap">
                    {currentLanguage.value[1]}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-center gap-2 pt-0.5 relative">
                <div className="h-[1px] w-7 bg-[#c78c42]/30 dark:bg-caramel-400/40" />
                <div className="w-1 h-1 rounded-full bg-[#c78c42] dark:bg-caramel-400 shadow-sm" />
                <div className="h-[1px] w-7 bg-[#c78c42]/30 dark:bg-caramel-400/40" />

                {/* Small language indicator next to divider */}
                <div
                  className={`flex items-center gap-1 ml-2 transition-opacity duration-500 ${
                    isTransitioning ? "opacity-0" : "opacity-100"
                  }`}
                >
                  <span className="text-[10px] opacity-40">
                    {currentLanguage.flag}
                  </span>
                  <span className="text-[8px] text-[#1c1e0e]/30 dark:text-gray-500 font-light tracking-wider uppercase">
                    {currentLanguage.language}
                  </span>
                </div>
              </div>

              <h2 className="text-xs text-[#1c1e0e]/70 dark:text-gray-300 font-light tracking-widest uppercase transition-colors duration-500">
                {t("hero.tagline")}
              </h2>
            </div>

            <p className="text-xs text-[#1c1e0e]/60 dark:text-gray-400 leading-relaxed font-light max-w-sm mx-auto transition-colors duration-500">
              {t("hero.description")}
            </p>

            <div className="flex flex-row gap-2 pt-1 justify-center">
              <button
                onClick={scrollToRooms}
                className="group relative px-4 py-2 text-white font-medium rounded-md shadow-[0_2px_14px_rgba(90,107,46,0.3)] hover:shadow-[0_4px_20px_rgba(90,107,46,0.45)] transition-all duration-300 flex items-center justify-center gap-1.5 text-xs overflow-hidden bg-gradient-to-r from-[#4f6026] via-[#6f8535] via-[#8c9f45] to-[#4f6026]"
              >
                {/* Moving Light Wave */}
                <span
                  className="absolute inset-0 w-3/4 h-full bg-gradient-to-r from-transparent via-white/35 to-transparent pointer-events-none animate-[light-wave_3.2s_cubic-bezier(0.4,0,0.2,1)_infinite]"
                  aria-hidden="true"
                />
                <span className="relative z-10 font-semibold">{t("hero.exploreRoomsMobile")}</span>
                <ArrowRight
                  className="relative z-10 w-3 h-3 group-hover:translate-x-1 transition-transform duration-300"
                  strokeWidth={2}
                />
              </button>

              <button
                onClick={() =>
                  document
                    .getElementById("contact")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="px-4 py-2 bg-white/70 backdrop-blur-sm hover:bg-white dark:bg-gray-700/70 dark:hover:bg-gray-700 text-[#1c1e0e] dark:text-gray-200 font-medium rounded-md shadow-[0_2px_12px_rgba(28,30,14,0.08)] hover:shadow-[0_3px_16px_rgba(28,30,14,0.12)] transition-all duration-300 border border-[#1c1e0e]/10 dark:border-gray-600/30 hover:border-[#5a6b2e]/30 dark:hover:border-olive-500/50 text-xs"
              >
                {t("hero.contactUs")}
              </button>
            </div>
          </div>
        </div>

        {/* Desktop/Tablet Layout - Horizontal */}
        <div className="hidden md:grid grid-cols-[1fr_auto] gap-8 lg:gap-16 items-center">
          <div className="flex flex-col space-y-3 md:space-y-4 text-left min-w-0">
            <div className="space-y-2 md:space-y-3">
              <div
                className={`transition-all duration-500 ${
                  isTransitioning
                    ? "opacity-0 blur-sm scale-95"
                    : "opacity-100 blur-0 scale-100"
                } ${fontScale.desktop} origin-left inline-block`}
              >
                <div
                  className="font-serif leading-[0.9]"
                  dir={isRTL ? "rtl" : "ltr"}
                  aria-hidden="true"
                >
                  <span className="block text-[#1c1e0e] dark:text-gray-100 text-4xl md:text-5xl lg:text-7xl xl:text-8xl font-semibold tracking-tight transition-colors duration-500 whitespace-nowrap">
                    {currentLanguage.value[0]}
                  </span>
                  <span className="block text-[#5a6b2e] dark:text-olive-400 text-4xl md:text-5xl lg:text-7xl xl:text-8xl font-semibold tracking-tight transition-colors duration-500 whitespace-nowrap">
                    {currentLanguage.value[1]}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-0.5">
                <div className="h-[1px] w-6 bg-[#1c1e0e]/20 dark:bg-gray-400/30" />
                <div className="w-1 h-1 rounded-full bg-[#c78c42] dark:bg-caramel-400" />
                <div className="h-[1px] w-6 bg-[#1c1e0e]/20 dark:bg-gray-400/30" />

                {/* Small language indicator next to divider */}
                <div
                  className={`flex items-center gap-1 ml-2 transition-opacity duration-500 ${
                    isTransitioning ? "opacity-0" : "opacity-100"
                  }`}
                >
                  <span className="text-[10px] opacity-40">
                    {currentLanguage.flag}
                  </span>
                  <span className="text-[8px] text-[#1c1e0e]/30 dark:text-gray-500 font-light tracking-wider uppercase">
                    {currentLanguage.language}
                  </span>
                </div>
              </div>

              <h2 className="text-sm md:text-base lg:text-lg text-[#1c1e0e]/70 dark:text-gray-300 font-light tracking-wide transition-colors duration-500">
                {t("hero.tagline")}
              </h2>
            </div>

            <p className="text-[11px] md:text-xs lg:text-sm text-[#1c1e0e]/60 dark:text-gray-400 leading-relaxed font-light max-w-md transition-colors duration-500">
              {t("hero.description")}
            </p>

            <div className="flex flex-row gap-3 pt-1">
              <button
                onClick={scrollToRooms}
                className="group relative px-5 py-2 md:px-6 md:py-2.5 text-white font-medium rounded-md shadow-[0_2px_14px_rgba(90,107,46,0.3)] hover:shadow-[0_4px_20px_rgba(90,107,46,0.45)] transition-all duration-300 flex items-center justify-center gap-2 text-[11px] md:text-xs lg:text-sm overflow-hidden bg-gradient-to-r from-[#4f6026] via-[#6f8535] via-[#8c9f45] to-[#4f6026]"
              >
                {/* Moving Light Wave */}
                <span
                  className="absolute inset-0 w-3/4 h-full bg-gradient-to-r from-transparent via-white/35 to-transparent pointer-events-none animate-[light-wave_3.2s_cubic-bezier(0.4,0,0.2,1)_infinite]"
                  aria-hidden="true"
                />
                <span className="relative z-10 font-semibold">{t("hero.exploreRooms")}</span>
                <ArrowRight
                  className="relative z-10 w-3 h-3 md:w-3.5 md:h-3.5 group-hover:translate-x-1 transition-transform duration-300"
                  strokeWidth={2}
                />
              </button>

              <button
                onClick={() =>
                  document
                    .getElementById("contact")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="px-5 py-2 md:px-6 md:py-2.5 bg-white/70 backdrop-blur-sm hover:bg-white dark:bg-gray-700/70 dark:hover:bg-gray-700 text-[#1c1e0e] dark:text-gray-200 font-medium rounded-md shadow-[0_2px_12px_rgba(28,30,14,0.08)] hover:shadow-[0_3px_16px_rgba(28,30,14,0.12)] transition-all duration-300 border border-[#1c1e0e]/10 dark:border-gray-600/30 hover:border-[#5a6b2e]/30 dark:hover:border-olive-500/50 text-[11px] md:text-xs lg:text-sm"
              >
                {t("hero.contactUs")}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-center relative w-64 md:w-80 lg:w-[28rem] xl:w-[32rem] h-64 md:h-80 lg:h-[28rem] xl:h-[32rem] flex-shrink-0">
            {isDesktop && (
              <img
                src={logos[currentLogoIndex].replace(/\.webp$/, "-sm.webp")}
                srcSet={logoSrcSet(logos[currentLogoIndex])}
                sizes={LOGO_SIZES}
                alt="Dar Zaytoun"
                className="w-full h-full object-contain drop-shadow-[0_10px_40px_rgba(90,107,46,0.12)]"
                fetchPriority="high"
                width={512}
                height={512}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
